import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LangContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

function SyncStatusIcon({ status }) {
  if (status === 'running') return (
    <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#f97316', animation: 'pulse 1.5s ease-in-out infinite' }} />
  );
  if (status === 'success') return (
    <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#22c55e' }} />
  );
  return (
    <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#ef4444' }} />
  );
}

function formatDateTime(iso) {
  if (!iso) return 'Nunca';
  try {
    return new Date(iso).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

export default function SincronizacionPage({ syncStatus: externalStatus }) {
  const { apiFetch } = useAuth();
  const { t } = useLang();
  const [status, setStatus]     = useState(externalStatus);
  const [logs, setLogs]         = useState([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsPage, setLogsPage] = useState(1);
  const LOGS_PER_PAGE = 10;
  const [loading, setLoading]   = useState(false);
  const [syncing, setSyncing]   = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate]     = useState('');

  // Gap detection state
  const [gapInfo, setGapInfo]         = useState(null);
  const [checkingGaps, setCheckingGaps] = useState(false);
  const [healingGaps, setHealingGaps]   = useState(false);
  const [gapDays, setGapDays]           = useState(90);

  // Orphan rows state (brief P2#6 — "filas obsoletas")
  const [orphanInfo, setOrphanInfo]       = useState(null);
  const [orphanSelected, setOrphanSelected] = useState({});
  const [loadingOrphans, setLoadingOrphans] = useState(false);
  const [deletingOrphans, setDeletingOrphans] = useState(false);

  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayStr = yesterday.toISOString().slice(0, 10);

  const loadStatus = useCallback(async () => {
    try {
      const res = await apiFetch('/api/sync/status');
      if (res.ok) setStatus(await res.json());
    } catch (err) { console.warn("[SmartClockIn] silent catch:", err); }
  }, [apiFetch]);

  const loadLogs = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const res = await apiFetch(`/api/sync/logs?page=${page}&per_page=${LOGS_PER_PAGE}`);
      if (res.ok) {
        const data = await res.json();
        setLogs(data.data || []);
        setLogsTotal(data.total || 0);
        setLogsPage(page);
      }
    } catch (err) { console.warn("[SmartClockIn] silent catch:", err); }
    setLoading(false);
  }, [apiFetch]);

  const loadOrphans = useCallback(async () => {
    setLoadingOrphans(true);
    try {
      const res = await apiFetch('/api/sync/orphans');
      if (res.ok) {
        const data = await res.json();
        setOrphanInfo(data);
        setOrphanSelected({});
      }
    } catch (err) { console.warn("[SmartClockIn] silent catch:", err); }
    setLoadingOrphans(false);
  }, [apiFetch]);

  useEffect(() => {
    loadStatus();
    loadLogs(1);
    loadOrphans();
    const interval = setInterval(() => { loadStatus(); }, 10000);
    return () => clearInterval(interval);
  }, [loadStatus, loadLogs, loadOrphans]);

  const toggleOrphan = (id) => setOrphanSelected(s => ({ ...s, [id]: !s[id] }));
  const toggleAllOrphans = () => {
    const allSelected = (orphanInfo?.orphans || []).every(o => orphanSelected[o.id]);
    const next = {};
    if (!allSelected) (orphanInfo?.orphans || []).forEach(o => { next[o.id] = true; });
    setOrphanSelected(next);
  };

  const handleDeleteOrphans = async () => {
    const ids = Object.keys(orphanSelected).filter(id => orphanSelected[id]);
    if (ids.length === 0) { toast.warning('Selecciona al menos una fila'); return; }
    if (!window.confirm(`¿Borrar permanentemente ${ids.length} fila(s) obsoleta(s)? Esta acción no se puede deshacer.`)) return;
    setDeletingOrphans(true);
    try {
      const res = await apiFetch('/api/sync/orphans', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`${data.deleted} fila(s) borrada(s)`);
        loadOrphans();
      } else {
        toast.error('Error al borrar las filas obsoletas');
      }
    } catch {
      toast.error('Error de conexión');
    }
    setDeletingOrphans(false);
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const body = {};
      if (startDate) body.start_date = startDate;
      if (endDate)   body.end_date   = endDate;
      const res = await apiFetch('/api/sync/run', {
        method: 'POST',
        body: JSON.stringify(body)
      });
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        if (data.auto_chunked) {
          toast.success(
            `Rango grande: chunked en ${data.estimated_chunks} bloques de ${data.chunk_days}d`,
            { description: 'Se procesará con reintentos automáticos en background.' }
          );
        } else {
          toast.success('Sincronización iniciada', { description: 'El proceso se está ejecutando en segundo plano' });
        }
        setTimeout(() => { loadStatus(); loadLogs(1); }, 2000);
      } else if (res.status === 409) {
        const data = await res.json().catch(() => ({}));
        toast.warning('Sincronización ya en curso', {
          description: data.detail || 'Espera a que termine el proceso actual antes de lanzar otro.',
        });
        loadStatus();
      } else {
        toast.error('Error al iniciar sincronización');
      }
    } catch {
      toast.error('Error de conexión');
    }
    setSyncing(false);
  };

  const handleCheckGaps = async () => {
    setCheckingGaps(true);
    try {
      const res = await apiFetch(`/api/sync/detect-gaps?days=${gapDays}`);
      if (res.ok) {
        const data = await res.json();
        setGapInfo(data);
        if (data.days_missing === 0) {
          toast.success('Sin huecos detectados', { description: `Todos los días en los últimos ${gapDays} días tienen datos.` });
        } else {
          toast.warning(`${data.days_missing} día(s) sin datos detectados`, { description: 'Pulsa "Rellenar huecos" para sincronizarlos.' });
        }
      } else {
        toast.error('Error al detectar huecos');
      }
    } catch {
      toast.error('Error de conexión');
    }
    setCheckingGaps(false);
  };

  const handleHealGaps = async () => {
    if (!window.confirm(`Esto sincronizará todos los días sin datos de los últimos ${gapDays} días. Puede tardar varios minutos. ¿Continuar?`)) return;
    setHealingGaps(true);
    try {
      const res = await apiFetch(`/api/sync/heal-gaps?days=${gapDays}`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        if (data.nothing_to_do) {
          toast.success('Sin huecos que rellenar', { description: data.message });
        } else {
          toast.success(`Rellenando ${data.missing_count} día(s) en background`, {
            description: `Modo: ${data.mode}. Revisa el historial para progreso.`
          });
          setGapInfo(null); // force recheck after
        }
        setTimeout(() => { loadStatus(); loadLogs(1); loadOrphans(); }, 2000);
      } else if (res.status === 409) {
        const data = await res.json().catch(() => ({}));
        toast.warning('Sincronización ya en curso', {
          description: data.detail || 'Espera a que termine el proceso actual antes de lanzar otro.',
        });
        loadStatus();
      } else {
        toast.error('Error al iniciar el relleno de huecos');
      }
    } catch {
      toast.error('Error de conexión');
    }
    setHealingGaps(false);
  };

  const lastSync = status?.last_sync;

  return (
    <div style={{ padding: '32px', overflowY: 'auto' }}>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontFamily: 'Space Grotesk', fontSize: '24px', fontWeight: 700, color: '#f1f5f9', margin: 0 }}>{t('sincronizacion')}</h1>
        <p style={{ color: '#64748b', fontSize: '14px', marginTop: '4px' }}>Control de sincronización SFTP + RDS</p>
      </div>

      {/* Status Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        {[
          { label: 'Empleados activos', value: status?.employees_count?.toLocaleString('es-ES') || '0', color: '#60a5fa' },
          { label: 'Registros KPI', value: status?.kpis_count?.toLocaleString('es-ES') || '0', color: '#34d399' },
          { label: 'SFTP', value: status?.sftp_configured ? 'Configurado' : 'No config.', color: status?.sftp_configured ? '#22c55e' : '#ef4444' },
          { label: 'RDS', value: status?.rds_configured ? 'Configurado' : 'No config.', color: status?.rds_configured ? '#22c55e' : '#ef4444' },
        ].map((card, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '12px', padding: '16px 20px' }}
          >
            <div style={{ color: '#64748b', fontSize: '12px', marginBottom: '8px' }}>{card.label}</div>
            <div style={{ color: card.color, fontSize: '22px', fontWeight: 700, fontFamily: 'Space Grotesk' }}>{card.value}</div>
          </motion.div>
        ))}
      </div>

      {/* Manual Sync Panel */}
      <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '12px', padding: '24px', marginBottom: '24px' }}>
        <h3 style={{ fontFamily: 'Space Grotesk', fontSize: '16px', fontWeight: 600, color: '#f1f5f9', marginBottom: '16px' }}>Sincronización manual</h3>

        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '16px' }}>
          <div>
            <label style={{ color: '#64748b', fontSize: '12px', display: 'block', marginBottom: '6px' }}>Fecha inicio</label>
            <input
              type="date"
              value={startDate || yesterdayStr}
              onChange={e => setStartDate(e.target.value)}
              style={{ background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9', borderRadius: '8px', padding: '8px 12px', fontSize: '13px', outline: 'none' }}
            />
          </div>
          <div>
            <label style={{ color: '#64748b', fontSize: '12px', display: 'block', marginBottom: '6px' }}>Fecha fin</label>
            <input
              type="date"
              value={endDate || yesterdayStr}
              onChange={e => setEndDate(e.target.value)}
              style={{ background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9', borderRadius: '8px', padding: '8px 12px', fontSize: '13px', outline: 'none' }}
            />
          </div>
          <Button
            data-testid="sync-now-button"
            onClick={handleSync}
            disabled={syncing || status?.is_running}
            style={{ background: '#2563eb', color: 'white', height: '40px', padding: '0 24px', display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            {(syncing || status?.is_running) ? (
              <>
                <div style={{ width: '14px', height: '14px', border: '2px solid rgba(255,255,255,0.3)', borderTop: '2px solid white', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                {t('syncRunning')}
              </>
            ) : (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="23 4 23 10 17 10"/>
                  <polyline points="1 20 1 14 7 14"/>
                  <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                </svg>
                {t('syncNow')}
              </>
            )}
          </Button>
        </div>

        {/* Last sync info */}
        {lastSync && (
          <div style={{ background: '#0f172a', border: '1px solid #1e3a5f', borderRadius: '8px', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <SyncStatusIcon status={lastSync.status} />
              <span style={{ color: '#94a3b8', fontSize: '13px', fontWeight: 600 }}>Estado: {lastSync.status}</span>
              <span style={{ color: '#475569', fontSize: '12px' }}>• {lastSync.date_range}</span>
            </div>
            <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
              <span data-testid="sync-last-text" style={{ color: '#64748b', fontSize: '12px' }}>
                Iniciada: <span style={{ color: '#94a3b8', fontFamily: 'IBM Plex Mono' }}>{formatDateTime(lastSync.started_at)}</span>
              </span>
              {lastSync.finished_at && (
                <span style={{ color: '#64748b', fontSize: '12px' }}>
                  Finalizada: <span style={{ color: '#94a3b8', fontFamily: 'IBM Plex Mono' }}>{formatDateTime(lastSync.finished_at)}</span>
                </span>
              )}
              {lastSync.employees_synced > 0 && (
                <span style={{ color: '#64748b', fontSize: '12px' }}>
                  Empleados: <span style={{ color: '#34d399', fontFamily: 'IBM Plex Mono' }}>{lastSync.employees_synced}</span>
                </span>
              )}
              {lastSync.kpis_computed > 0 && (
                <span style={{ color: '#64748b', fontSize: '12px' }}>
                  KPIs: <span style={{ color: '#60a5fa', fontFamily: 'IBM Plex Mono' }}>{lastSync.kpis_computed}</span>
                </span>
              )}
            </div>
            {lastSync.messages?.length > 0 && (
              <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                {lastSync.messages.slice(-4).map((m, i) => (
                  <div key={i} style={{ color: '#475569', fontSize: '11px', fontFamily: 'IBM Plex Mono' }}>
                    <span style={{ color: '#1e3a5f' }}>{m.ts?.slice(11, 19)}</span> {m.msg}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Gap detection & heal panel */}
      <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '12px', padding: '24px', marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2">
            <path d="M12 9v4M12 17h.01"/>
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
          </svg>
          <h3 style={{ fontFamily: 'Space Grotesk', fontSize: '16px', fontWeight: 600, color: '#f1f5f9', margin: 0 }}>
            Detectar y rellenar días sin datos
          </h3>
        </div>
        <p style={{ color: '#64748b', fontSize: '13px', margin: '0 0 16px 0' }}>
          Si alguna sincronización pasada falló (timeout RDS, pod reinicio…), aquí puedes detectar qué días faltan en la BD
          y relanzar la sincronización SOLO de esos días, con reintentos automáticos.
        </p>

        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '16px' }}>
          <div>
            <label style={{ color: '#64748b', fontSize: '12px', display: 'block', marginBottom: '6px' }}>Inspeccionar últimos N días</label>
            <Input
              data-testid="gap-days-input"
              type="number"
              min={1}
              max={365}
              value={gapDays}
              onChange={e => setGapDays(Math.max(1, Math.min(365, Number(e.target.value) || 1)))}
              style={{ background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9', width: '120px', height: '40px' }}
            />
          </div>
          <Button
            data-testid="gap-check-btn"
            onClick={handleCheckGaps}
            disabled={checkingGaps}
            style={{ background: '#0f172a', color: '#f1f5f9', height: '40px', padding: '0 18px', border: '1px solid #334155' }}
          >
            {checkingGaps ? 'Comprobando…' : 'Detectar huecos'}
          </Button>
          <Button
            data-testid="gap-heal-btn"
            onClick={handleHealGaps}
            disabled={healingGaps || status?.is_running}
            style={{ background: '#f59e0b', color: '#0f172a', height: '40px', padding: '0 18px', fontWeight: 600 }}
          >
            {healingGaps ? 'Iniciando…' : 'Rellenar huecos'}
          </Button>
        </div>

        {gapInfo && (
          <div style={{ background: '#0f172a', border: `1px solid ${gapInfo.days_missing > 0 ? '#7c2d12' : '#14532d'}`, borderRadius: '8px', padding: '14px 16px' }}>
            <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', marginBottom: gapInfo.days_missing > 0 ? '10px' : 0 }}>
              <div>
                <div style={{ color: '#64748b', fontSize: '11px' }}>Rango</div>
                <div style={{ color: '#94a3b8', fontSize: '13px', fontFamily: 'IBM Plex Mono' }}>
                  {gapInfo.range.start} → {gapInfo.range.end}
                </div>
              </div>
              <div>
                <div style={{ color: '#64748b', fontSize: '11px' }}>Días esperados</div>
                <div style={{ color: '#f1f5f9', fontSize: '15px', fontFamily: 'IBM Plex Mono', fontWeight: 600 }}>
                  {gapInfo.days_expected}
                </div>
              </div>
              <div>
                <div style={{ color: '#64748b', fontSize: '11px' }}>Con datos</div>
                <div style={{ color: '#22c55e', fontSize: '15px', fontFamily: 'IBM Plex Mono', fontWeight: 600 }}>
                  {gapInfo.days_with_data}
                </div>
              </div>
              <div>
                <div style={{ color: '#64748b', fontSize: '11px' }}>Sin datos</div>
                <div data-testid="gap-missing-count" style={{ color: gapInfo.days_missing > 0 ? '#ef4444' : '#22c55e', fontSize: '15px', fontFamily: 'IBM Plex Mono', fontWeight: 600 }}>
                  {gapInfo.days_missing}
                </div>
              </div>
            </div>
            {gapInfo.days_missing > 0 && (
              <div>
                <div style={{ color: '#94a3b8', fontSize: '12px', marginBottom: '6px' }}>Días faltantes:</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {gapInfo.missing_dates.slice(0, 30).map(d => (
                    <span
                      key={d}
                      style={{
                        background: '#451a03', color: '#fbbf24', fontFamily: 'IBM Plex Mono',
                        fontSize: '11px', padding: '3px 8px', borderRadius: '6px', border: '1px solid #7c2d12',
                      }}
                    >{d}</span>
                  ))}
                  {gapInfo.missing_dates.length > 30 && (
                    <span style={{ color: '#64748b', fontSize: '11px', alignSelf: 'center' }}>
                      … y {gapInfo.missing_dates.length - 30} más
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Logs Table */}
      <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '12px', padding: '24px' }}>
        <h3 style={{ fontFamily: 'Space Grotesk', fontSize: '16px', fontWeight: 600, color: '#f1f5f9', marginBottom: '16px' }}>{t('logsSync')}</h3>
        {loading ? (
          <div style={{ textAlign: 'center', color: '#64748b', padding: '40px' }}>Cargando logs...</div>
        ) : logs.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#64748b', padding: '40px' }}>Sin logs de sincronización</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table data-testid="sync-logs-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #334155' }}>
                  {['Fecha', 'Rango', 'Estado', 'Empleados', 'KPIs', 'Por'].map(h => (
                    <th key={h} style={{ padding: '8px 12px', textAlign: 'left', color: '#64748b', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {logs.map((log, i) => (
                  <tr key={log.id || log._id || `${log.started_at}-${i}`} className="table-row-hover" style={{ borderBottom: '1px solid #0f172a' }}>
                    <td style={{ padding: '10px 12px', color: '#94a3b8', fontSize: '12px', fontFamily: 'IBM Plex Mono', whiteSpace: 'nowrap' }}>
                      {formatDateTime(log.started_at)}
                    </td>
                    <td style={{ padding: '10px 12px', color: '#64748b', fontSize: '12px', fontFamily: 'IBM Plex Mono' }}>{log.date_range}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <SyncStatusIcon status={log.status} />
                        <span style={{ color: log.status === 'success' ? '#22c55e' : log.status === 'running' ? '#f97316' : '#ef4444', fontSize: '12px' }}>
                          {log.status}
                        </span>
                      </div>
                    </td>
                    <td style={{ padding: '10px 12px', color: '#34d399', fontSize: '12px', fontFamily: 'IBM Plex Mono', textAlign: 'center' }}>{log.employees_synced || 0}</td>
                    <td style={{ padding: '10px 12px', color: '#60a5fa', fontSize: '12px', fontFamily: 'IBM Plex Mono', textAlign: 'center' }}>{log.kpis_computed || 0}</td>
                    <td style={{ padding: '10px 12px', color: '#475569', fontSize: '12px' }}>{log.triggered_by}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {logsTotal > LOGS_PER_PAGE && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', marginTop: '16px' }}>
            <Button
              data-testid="sync-logs-prev-page"
              onClick={() => loadLogs(logsPage - 1)}
              disabled={logsPage <= 1 || loading}
              style={{ background: '#0f172a', color: '#f1f5f9', border: '1px solid #334155', height: '32px', padding: '0 14px', fontSize: '12px' }}
            >
              ← Anterior
            </Button>
            <span style={{ color: '#64748b', fontSize: '12px', fontFamily: 'IBM Plex Mono' }}>
              Página {logsPage} de {Math.max(1, Math.ceil(logsTotal / LOGS_PER_PAGE))} ({logsTotal} syncs)
            </span>
            <Button
              data-testid="sync-logs-next-page"
              onClick={() => loadLogs(logsPage + 1)}
              disabled={logsPage >= Math.ceil(logsTotal / LOGS_PER_PAGE) || loading}
              style={{ background: '#0f172a', color: '#f1f5f9', border: '1px solid #334155', height: '32px', padding: '0 14px', fontSize: '12px' }}
            >
              Siguiente →
            </Button>
          </div>
        )}
      </div>

      {/* Filas obsoletas (brief P2#6) */}
      {orphanInfo?.orphans?.length > 0 && (
        <div style={{ background: '#1e293b', border: '1px solid #7c2d12', borderRadius: '12px', padding: '24px', marginTop: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px', flexWrap: 'wrap', gap: '10px' }}>
            <h3 style={{ fontFamily: 'Space Grotesk', fontSize: '16px', fontWeight: 600, color: '#fbbf24', margin: 0 }}>
              Filas obsoletas detectadas ({orphanInfo.orphans.length})
            </h3>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button
                data-testid="orphans-refresh-btn"
                onClick={loadOrphans}
                disabled={loadingOrphans}
                style={{ background: '#0f172a', color: '#94a3b8', border: '1px solid #334155', height: '32px', padding: '0 12px', fontSize: '12px' }}
              >
                {loadingOrphans ? 'Actualizando…' : '↻ Actualizar'}
              </Button>
              <Button
                data-testid="orphans-delete-btn"
                onClick={handleDeleteOrphans}
                disabled={deletingOrphans}
                style={{ background: '#ef4444', color: '#fff', height: '32px', padding: '0 14px', fontSize: '12px', fontWeight: 600 }}
              >
                {deletingOrphans ? 'Borrando…' : `Borrar seleccionadas (${Object.values(orphanSelected).filter(Boolean).length})`}
              </Button>
            </div>
          </div>
          <p style={{ color: '#64748b', fontSize: '12px', margin: '0 0 14px' }}>
            Filas que existían en KPIs pero ya no aparecen en el último recálculo (empleado dado de baja, cambio de turno, etc.).
            Nunca se borran solas — revísalas y bórralas manualmente si procede.
          </p>
          <div style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', maxHeight: '320px', overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ background: '#1e293b', position: 'sticky', top: 0 }}>
                  <th style={{ padding: '8px 12px' }}>
                    <input type="checkbox" data-testid="orphans-select-all" onChange={toggleAllOrphans}
                      checked={orphanInfo.orphans.length > 0 && orphanInfo.orphans.every(o => orphanSelected[o.id])} />
                  </th>
                  <th style={{ padding: '8px 12px', textAlign: 'left', color: '#64748b' }}>Empleado</th>
                  <th style={{ padding: '8px 12px', textAlign: 'left', color: '#64748b' }}>Fecha</th>
                  <th style={{ padding: '8px 12px', textAlign: 'left', color: '#64748b' }}>Turno</th>
                  <th style={{ padding: '8px 12px', textAlign: 'left', color: '#64748b' }}>Tipo</th>
                </tr>
              </thead>
              <tbody>
                {orphanInfo.orphans.map((o) => (
                  <tr key={o.id} style={{ borderTop: '1px solid #1e293b' }}>
                    <td style={{ padding: '8px 12px' }}>
                      <input type="checkbox" data-testid={`orphan-checkbox-${o.id}`} checked={!!orphanSelected[o.id]} onChange={() => toggleOrphan(o.id)} />
                    </td>
                    <td style={{ padding: '8px 12px', color: '#f1f5f9' }}>{o.nombre} {o.apellidos} <span style={{ color: '#475569', fontFamily: 'IBM Plex Mono' }}>({o.id_employee})</span></td>
                    <td style={{ padding: '8px 12px', color: '#94a3b8', fontFamily: 'IBM Plex Mono' }}>{o.svc_date}</td>
                    <td style={{ padding: '8px 12px', color: '#94a3b8' }}>{o.shift_idx}</td>
                    <td style={{ padding: '8px 12px', color: o.is_telework ? '#a855f7' : '#64748b' }}>{o.is_telework ? 'Teletrabajo' : 'Presencial'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } } @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }`}</style>
    </div>
  );
}
